import { useQuery } from "@tanstack/react-query";
import styles from './styles.module.css';
import { IStaggeredListItem } from "../StaggeredList";
import { MutableRefObject, useMemo, useState, useRef, useEffect } from "react";
import { IUploadPopUpRef } from "../UploadPopUp";
import { segmentEventPayload,homepageFeatureAtom, presetItemAtom } from "@/modules/editor-v2/Atoms";
import { useSetAtom, useAtomValue, useAtom } from "jotai";
import { useRouter } from "next/router";
import { getHomePageMarketPlaces } from "@/modules/editor-v2/services/HomePage";
import { useSelector } from "react-redux";
import { TrackWebappEvent } from "src/modules/segment-event-tracking/segmentEventTracking";
import { EventTrackName } from "src/modules/segment-event-tracking/constant";
import GoogleDriveUploadPopUp from '../GoogleDrivePopUp';
import { EditorScreen } from "@/modules/batchEditor/BulkUploadEditor/constant";
import useToast from "@/hooks/useToast";
import { aiTaskDisableAtom } from "@/modules/batchEditor/atoms";
import { css } from "@emotion/react";
import DropSelect from "../DropSelect";
import DropSelectSkeleton from "../DropSelect/DropSelectSkeleton";

interface IMarketPlacesProps {
    popUpRef:MutableRefObject<IUploadPopUpRef>,
    userId:String
}

export default function StandardMarketplaces({popUpRef, userId}: IMarketPlacesProps) {
    const {data, isLoading:marketPlaceLoading, isError} = useQuery(
        ["marketplaces", userId],
        getHomePageMarketPlaces
    );
    const getSegmentPayloadAtom = useAtomValue(segmentEventPayload);
    const router = useRouter();
    const setHomepageFeature = useSetAtom(homepageFeatureAtom);
    const strapiData:any = useSelector<any>(state=>state.strapi?.entireStrapiData?.WebappHomepageV22?.WebappHomepageV22);
    const strapiData2:any = useSelector<any>(state=>state.strapi?.entireStrapiData?.StaggeredListItemType?.StaggeredListItemType);
    const marketPlacesStrapiData:any = useSelector<any>(state=>state.strapi?.entireStrapiData?.StaggeredListItemType?.StaggeredListItemType?.ListItemType?.filter((item)=> item.listTypeName === "Marketplaces"));
    const googleDrivePopUpRef = useRef<any>();
    const [marketplaceId, setMarketplaceId] = useState('');
    const setAiTaskDisable = useSetAtom(aiTaskDisableAtom);
    const toaster = useToast();
    const [presetItem, setPresetItem] = useAtom<any>(presetItemAtom);

    const items:IStaggeredListItem[] = useMemo(()=>data?.map(item=> ({
        id: item?.marketplace_id,
        title:item?.name, 
        image:item?.logo,
        widgetImage: marketPlacesStrapiData?.find((marketplace)=> marketplace.title.toLowerCase() === item?.name.toLowerCase())?.widgetImage || "https://dresma-assets.s3.us-east-2.amazonaws.com/DoMyShoot/App+Screens/501.png",
        extraData: {marketplaceId: item?.marketplace_id, size: item?.size},
        aiTaskDisable: item?.aiTaskDisable,
        type: item?.type,
      })
    ).filter(item => item.type === 'Standard'),[data]);

    const onUploadSuccessGenerator = (item) => async ({batchId="", initiatedSource, jobId=""})=>{
        if (item == undefined) item = presetItem;
        if ( item?.aiTaskDisable || item?.aiTaskDisable) {
            setAiTaskDisable(true);
            router.push(`/dashboard/batch-editor?mode=${EditorScreen.PRODUCT_LIFESTYLE}&batchAI=${batchId}`);
            sessionStorage.removeItem('videoUploadOnAiTaskDisable');
            return;
        } else if (sessionStorage.getItem('videoUploadOnAiTaskDisable') && !item?.aiTaskDisable) {
            toaster.error("Cannot upload Video Formats on Selected Preset");
            sessionStorage.removeItem('videoUploadOnAiTaskDisable');
            setPresetItem(null);
            return;
        }
        if(!item?.title) {
            const presetDetails = {
                size: {
                    height: 1500,
                    width: 1500
                },
                coverage: 80,
                position: "centre",
                backgroundColor: "#FFFFFF",
            }
            jobId ? router.push(`/dashboard/batch-editor/${jobId}?marketplaceSource=${"Custom"}&presetDetails=${encodeURIComponent(JSON.stringify(presetDetails))}&initiatedSource=marketplace`) 
            : router.push(`/dashboard/batch-editor?batchMode=multiple&batchJobId=${batchId}&marketplaceSource=${"Custom"}&presetDetails=${encodeURIComponent(JSON.stringify(presetDetails))}&initiatedSource=marketplace`);
            popUpRef?.current?.close();
            return;
        }
       
        jobId ? router.push(`/dashboard/batch-editor/${jobId}?marketplaceSource=${item?.title}&marketplaceId=${item?.extraData?.marketplaceId}`) 
        : router.push(
          `/dashboard/batch-editor?batchMode=multiple&batchJobId=${batchId}&marketplaceSource=${item?.title}&marketplaceId=${item?.extraData?.marketplaceId}&initiatedSource=marketplace`
        );
        popUpRef?.current?.close();
        return;
    }

    const onItemClick = (item) => {
        setMarketplaceId(item?.extraData?.marketplaceId)
        setHomepageFeature(item?.title);
        const eventPayload = {
            homepageFeature: item?.title,
            ...getSegmentPayloadAtom
          };
        TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_marketplace_hp}`, eventPayload);
        popUpRef.current.open({
            title: item?.title || "Custom",
            onUploadSuccess: onUploadSuccessGenerator(item),
            widgetImage: item?.widgetImage,
            batchEditorJobPopUp: true,
            marketplaceId: item?.extraData?.marketplaceId,
            aiTaskDisable: item?.aiTaskDisable,
            onGoogleDriveUpload: onGoogleDriveUploadGenerator(item)
        })
    }

    const onGoogleDriveUploadGenerator = (item) => async ()  =>{
        setPresetItem(item);
        googleDrivePopUpRef?.current?.open(
            {
                onUploadSuccess: onUploadSuccessGenerator(item),
            });
        window.setTimeout(()=>popUpRef.current.close(),100);
    }

    return <div className={styles.container}>
        <div className={styles.heading}>
            {strapiData?.MarketplaceSectionHeading || "Adapt your images"}
        </div>
        {
            marketPlaceLoading 
                ? <DropSelectSkeleton />
                : (
                <div css={emotionStyles.container}>
                  <DropSelect dropDownTitle={strapiData2?.SelectMarketplace || "Select Marketplace"} items={items} onItemClick={onItemClick} />
                  <GoogleDriveUploadPopUp
                    ref={googleDrivePopUpRef}
                    onUploadSuccess={onUploadSuccessGenerator()}
                    onBackPress={()=>{}}
                    marketPlaceIntiatedSource={marketplaceId}
                    initiatedSource={'marketplace'}
                  />       
                </div>)
        }
    </div>
}

const emotionStyles = {
    container: css`
        & .slick-track {
            display: inline-flex;
            align-items: flex-end;
            gap: 8px;
        }
    `,
    containerBox:css`
        width:100%;
        overflow-X:scroll;
        display:flex;
        gap:16px;
        align-items:flex-end;
        flex-wrap:nowrap;
        padding:20px 10px;
        ::-webkit-scrollbar: {
            display: 'none' !important;
        }   
    `,
    rightArrow:css`
        box-shadow: 0px 0px 16px 0px #0000003D;
        width: 40px;
        height: 40px;
        padding: 8px, 8px, 8px, 8px;
        border-radius: 1000px;
        background-color:white;
        position:absolute;
        right:0;
        top:50%;
        transform:translate(25%, -50%);
        z-index:1;
        cursor:pointer;
        display:flex;
        justify-content:center;
        align-items:center;
    `,
    leftArrow:css`
        box-shadow: 0px 0px 16px 0px #0000003D;
        width: 40px;
        height: 40px;
        padding: 8px, 8px, 8px, 8px;
        border-radius: 1000px;
        background-color:white;
        position:absolute;
        left:0;
        top:50%;
        transform:translate(-50%, -50%) rotate(-180deg);
        z-index:6;
        cursor:pointer;
        display:flex;
        justify-content:center;
        align-items:center;
    `,
}